import { useState } from "react";

import {
  Button,
  Color,
  Combobox,
  DateCalendar,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";
import { type DatesRangeValue } from "@vericus/cadmus-ui";

import clsx from "clsx";
import { format } from "date-fns";

import { DateRange } from "@/features/project-management/types";
import { getLocalDate } from "@/utils/datetime";

import * as styles from "./date-range-filter.css";

/**
 * Properties to customise the highlight for a single date.
 * key is the date in ISO8601 (e.g. '2020-04-17' ) and value is color highlight
 */
export const dayHighlightKey = (d: Date) => format(d, "yyyy-MM-dd");
/**
 * Props for the DateRangeFilter component.
 */
interface DateRangeFilterProps {
  /**
   * The label for the date range filter.
   */
  label: string;

  /**
   * The currently selected date range. Optional.
   * An array containing the start and end dates. Each date can be a Date object or null.
   */
  selectedDateRange: DateRange;

  /**
   * The callback function to handle the date range change.
   */
  onDateRangeChange: (dateRange: DateRange) => void;

  /**
   * The class name for the date range filter.
   */
  className?: string;
}

/**
 * Date Range Filter
 *
 * This component is used to select a date range for the activity report.
 * If will have a dropdown to select the date range and a button to apply the date range.
 */
export function DateRangeFilter({
  label,
  selectedDateRange,
  onDateRangeChange,
  className,
}: DateRangeFilterProps) {
  const todayKey = dayHighlightKey(new Date());
  const highlightDays: { [key: string]: Color[] } = {};
  highlightDays[todayKey] = ["cyan500"];

  const [value, setValue] = useState<DatesRangeValue>([
    selectedDateRange[0],
    selectedDateRange[0],
  ]);

  const getDisplayText = () => {
    if (selectedDateRange[0] && selectedDateRange[1]) {
      return `${format(selectedDateRange[0], "MMM d")} - ${format(selectedDateRange[1], "MMM d")}`;
    } else if (selectedDateRange[0]) {
      return `From ${format(selectedDateRange[0], "MMM d")}`;
    } else if (selectedDateRange[1]) {
      return `Until ${format(selectedDateRange[1], "MMM d")}`;
    }
    return label;
  };

  return (
    <Combobox.Root>
      <Combobox.Trigger asChild>
        <SelectButton className={clsx(styles.filterSelectButton, className)}>
          {getDisplayText()}
        </SelectButton>
      </Combobox.Trigger>
      <Combobox.Content align="end" style={{ width: "100%" }}>
        <div className={styles.datePickerContainer}>
          <Text kind="headingSix">{label}</Text>
          <DateDropdown
            value={[
              value[0] != null ? new Date(value[0]) : null,
              value[1] != null ? new Date(value[1]) : null,
            ]}
            setValue={setValue}
          />
          <Button
            kind="secondary"
            onClick={() =>
              onDateRangeChange([
                value[0] != null ? new Date(value[0]) : null,
                value[1] != null ? new Date(value[1]) : null,
              ])
            }
          >
            Apply
          </Button>
        </div>
      </Combobox.Content>
    </Combobox.Root>
  );
}

function DateDropdown({
  value,
  setValue,
}: {
  value: DateRange;
  setValue: (value: DateRange) => void;
}) {
  return (
    <Combobox.Root>
      <Combobox.Trigger asChild>
        <div className={styles.selectionContainer}>
          <SelectButton>
            {value[0] ? format(value[0], "MMM d, yyyy") : "From"}
          </SelectButton>
          <SelectButton>
            {value[1] ? format(value[1], "MMM d, yyyy") : "To"}
          </SelectButton>
        </div>
      </Combobox.Trigger>
      <Combobox.Content align="center" style={{ width: "100%", padding: 10 }}>
        <DateCalendar
          mode="range"
          size="md"
          shadow={false}
          weekendDays={[]}
          withCellSpacing={false}
          maxLevel="month"
          value={[
            value[0] != null ? format(value[0], "yyyy-MM-dd") : null,
            value[1] != null ? format(value[1], "yyyy-MM-dd") : null,
          ]}
          onChange={(value) => {
            const [from, to] = value as [string | null, string | null];

            setValue([
              from != null ? getLocalDate(from) : null,
              to != null ? getLocalDate(to) : null,
            ]);
          }}
          maxDate={new Date()}
          numberOfColumns={1}
        />
      </Combobox.Content>
    </Combobox.Root>
  );
}
